import React, { useEffect, useState } from 'react';
import { QuestionRendererProps } from './types';
import { AnalogClock } from '../../visuals/AnalogClock';
import { Clock } from 'lucide-react';
export function ClockRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  // Parse target time from question options or context if needed
  // But for this renderer, we expect the question to provide the time to display
  // The 'correctAnswer' in QuestionCore is an index, but for interactive questions
  // we often use the options array to store the target value or parse it from the question text.
  // For 'klok_aflezen', we'll assume the question text or context contains the time,
  // OR better: we use the options array to store the correct time string as the first option
  // and distractors as others.
  // Let's parse the time to display from the question data
  // We'll assume the correct answer is stored in options[question.correctAnswer]
  // Format expected: "HH:MM:SS" or "HH:MM"
  const targetTimeStr = question.options[question.correctAnswer];
  const [targetHours, targetMinutes, targetSeconds] = targetTimeStr.split(':').map(Number);
  // User input state
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [seconds, setSeconds] = useState('');
  // Update ready state when inputs change
  useEffect(() => {
    const isReady = hours !== '' && minutes !== '' && (!targetSeconds ? true : seconds !== ''); // Only require seconds if target has them
    onReadyChange(isReady);
  }, [hours, minutes, seconds, targetSeconds, onReadyChange]);
  // Handle validation trigger
  useEffect(() => {
    if (shouldValidate) {
      // Format answer as HH:MM:SS
      const h = hours.padStart(2, '0');
      const m = minutes.padStart(2, '0');
      const s = targetSeconds !== undefined ? seconds.padStart(2, '0') : '00';
      const answerStr = `${h}:${m}:${s}`;
      onAnswer(answerStr);
    }
  }, [shouldValidate, hours, minutes, seconds, targetSeconds, onAnswer]);
  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>, value: string, max: number) => {
    // Allow empty string or numbers only
    if (value === '' || /^\d+$/.test(value) && parseInt(value) <= max) {
      setter(value);
    }
  };
  return <div className="flex flex-col items-center w-full max-w-2xl mx-auto" data-id="element-1149">
      <div className="mb-8 p-6 bg-slate-50 rounded-2xl border border-slate-200 shadow-inner" data-id="element-1150">
        <AnalogClock hours={targetHours} minutes={targetMinutes} seconds={targetSeconds || 0} showSeconds={targetSeconds !== undefined} size={280} data-id="element-1151" />
      </div>

      <div className="w-full bg-white p-6 rounded-xl border-2 border-indigo-100 shadow-sm" data-id="element-1152">
        <h3 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2" data-id="element-1153">
          <Clock className="w-5 h-5 text-indigo-600" data-id="element-1154" />
          Hoe laat is het?
        </h3>

        <div className="flex items-center justify-center gap-2 sm:gap-4" data-id="element-1155">
          {/* Hours Input */}
          <div className="flex flex-col items-center" data-id="element-1156">
            <label className="text-xs font-bold text-slate-500 mb-1 uppercase tracking-wide" data-id="element-1157">
              Uur
            </label>
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={hours} onChange={e => handleInputChange(setHours, e.target.value, 23)} placeholder="00" className="w-16 h-16 sm:w-20 sm:h-20 text-center text-2xl sm:text-3xl font-bold rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300" maxLength={2} data-id="element-1158" />
          </div>

          <div className="text-3xl font-bold text-slate-300 mt-6" data-id="element-1159">:</div>

          {/* Minutes Input */}
          <div className="flex flex-col items-center" data-id="element-1160">
            <label className="text-xs font-bold text-slate-500 mb-1 uppercase tracking-wide" data-id="element-1161">
              Min
            </label>
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={minutes} onChange={e => handleInputChange(setMinutes, e.target.value, 59)} placeholder="00" className="w-16 h-16 sm:w-20 sm:h-20 text-center text-2xl sm:text-3xl font-bold rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300" maxLength={2} data-id="element-1162" />
          </div>

          {/* Seconds Input (Conditional) */}
          {targetSeconds !== undefined && <>
              <div className="text-3xl font-bold text-slate-300 mt-6" data-id="element-1163">:</div>
              <div className="flex flex-col items-center" data-id="element-1164">
                <label className="text-xs font-bold text-slate-500 mb-1 uppercase tracking-wide" data-id="element-1165">
                  Sec
                </label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" value={seconds} onChange={e => handleInputChange(setSeconds, e.target.value, 59)} placeholder="00" className="w-16 h-16 sm:w-20 sm:h-20 text-center text-2xl sm:text-3xl font-bold rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-4 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 text-red-600" maxLength={2} data-id="element-1166" />
              </div>
            </>}
        </div>

        <p className="text-center text-slate-400 text-sm mt-4" data-id="element-1167">
          Vul de tijd in zoals op de klok (uu:mm
          {targetSeconds !== undefined ? ':ss' : ''})
        </p>
      </div>
    </div>;
}